import { create } from 'zustand'

export type Overlay = 'power' | 'heat' | 'water' | 'data'
export type LevelId = 'L1' | 'L2' | 'L3' | 'L4' | 'L5' | 'L6' | 'L7' | 'L8'
export const LEVELS: LevelId[] = ['L1', 'L2', 'L3', 'L4', 'L5', 'L6', 'L7', 'L8']

export interface NavigateAction {
  view: 'inside' | 'facility' | 'map' | 'state'
  level?: LevelId
  overlay?: Overlay
  hotspot?: string
  slug?: string
  state?: string
  lat?: number
  lng?: number
  zoom?: number
}

function readPref<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key)
    return v === null ? fallback : (JSON.parse(v) as T)
  } catch {
    return fallback
  }
}
function writePref(key: string, v: unknown) {
  try { localStorage.setItem(key, JSON.stringify(v)) } catch { /* storage may be unavailable */ }
}

interface AppState {
  theme: 'dark' | 'light'
  setTheme: (t: 'dark' | 'light') => void
  textOnly: boolean
  setTextOnly: (v: boolean) => void
  // Inside
  level: LevelId
  setLevel: (l: LevelId) => void
  overlays: Set<Overlay>
  toggleOverlay: (o: Overlay, on?: boolean) => void
  focusHotspot: string | null
  setFocusHotspot: (id: string | null) => void
  explode: boolean
  setExplode: (v: boolean) => void
  // Guide
  guideOpen: boolean
  setGuideOpen: (v: boolean) => void
  guideContext: Record<string, unknown>
  setGuideContext: (c: Record<string, unknown>) => void
  pendingNav: NavigateAction | null
  setPendingNav: (a: NavigateAction | null) => void
  guidePrefill: string | null
  setGuidePrefill: (s: string | null) => void
}

export const useApp = create<AppState>((set, get) => ({
  // The paper world (DESIGN.md) is light-only; the old dark theme is retired.
  theme: 'light',
  setTheme: (theme) => { writePref('gh.theme', theme); document.documentElement.dataset.theme = theme; set({ theme }) },
  textOnly: readPref('gh.textOnly', false),
  setTextOnly: (textOnly) => { writePref('gh.textOnly', textOnly); set({ textOnly }) },
  level: 'L1',
  setLevel: (level) => set({ level, focusHotspot: null, explode: false }),
  overlays: new Set<Overlay>(),
  toggleOverlay: (o, on) => {
    const next = new Set(get().overlays)
    const want = on ?? !next.has(o)
    if (want) next.add(o)
    else next.delete(o)
    set({ overlays: next })
  },
  focusHotspot: null,
  setFocusHotspot: (focusHotspot) => set({ focusHotspot }),
  explode: false,
  setExplode: (explode) => set({ explode }),
  guideOpen: false,
  setGuideOpen: (guideOpen) => set({ guideOpen }),
  guideContext: {},
  setGuideContext: (guideContext) => set({ guideContext }),
  pendingNav: null,
  setPendingNav: (pendingNav) => set({ pendingNav }),
  guidePrefill: null,
  setGuidePrefill: (guidePrefill) => set({ guidePrefill }),
}))

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
}

let glCache: 'webgl2' | 'webgl' | 'none' | null = null
/** Probe once per page: every probe creates a WebGL context, and browsers cap how many can be alive. */
export function webglSupport(): 'webgl2' | 'webgl' | 'none' {
  if (glCache) return glCache
  glCache = 'none'
  try {
    const c = document.createElement('canvas')
    const g2 = c.getContext('webgl2')
    const ctx = (g2 ?? c.getContext('webgl')) as WebGLRenderingContext | null
    if (ctx) {
      glCache = g2 ? 'webgl2' : 'webgl'
      ctx.getExtension('WEBGL_lose_context')?.loseContext()
    }
  } catch { /* no canvas */ }
  return glCache
}
